import React from 'react';
import { Printer, Download, Send } from 'lucide-react';
import './InvoicePdfPanel.css';

// ── Line items (Apr 1–Apr 15 at Kingston Health SC) ──────────────────────────
// David Chen's rate reflects the $85/hr update from Phase 3.

interface LineItem {
    readonly date: string;
    readonly staff: string;
    readonly position: string;
    readonly startTime: string;
    readonly endTime: string;
    readonly totalHours: string;
    readonly hourlyRate: string;
    readonly totalAmt: string;
    readonly isDavidChen: boolean;
}

const LINE_ITEMS: readonly LineItem[] = [
    { date: '2026-04-01', staff: 'David Chen',    position: 'Physiotherapist',  startTime: '09:00', endTime: '17:00', totalHours: '8.0', hourlyRate: '$85.00', totalAmt: '$680.00',   isDavidChen: true },
    { date: '2026-04-03', staff: 'David Chen',    position: 'Physiotherapist',  startTime: '09:00', endTime: '17:00', totalHours: '8.0', hourlyRate: '$85.00', totalAmt: '$680.00',   isDavidChen: true },
    { date: '2026-04-07', staff: 'David Chen',    position: 'Physiotherapist',  startTime: '09:00', endTime: '17:00', totalHours: '8.0', hourlyRate: '$85.00', totalAmt: '$680.00',   isDavidChen: true },
    { date: '2026-04-09', staff: 'David Chen',    position: 'Physiotherapist',  startTime: '09:00', endTime: '17:00', totalHours: '8.0', hourlyRate: '$85.00', totalAmt: '$680.00',   isDavidChen: true },
    { date: '2026-04-10', staff: 'Sarah Mitchell', position: 'Registered Nurse', startTime: '07:00', endTime: '19:00', totalHours: '12.0', hourlyRate: '$87.00', totalAmt: '$1,044.00', isDavidChen: false },
    { date: '2026-04-11', staff: 'Sarah Mitchell', position: 'Registered Nurse', startTime: '07:00', endTime: '19:00', totalHours: '12.0', hourlyRate: '$87.00', totalAmt: '$1,044.00', isDavidChen: false },
    { date: '2026-04-14', staff: 'Maria Santos',   position: 'Registered Nurse', startTime: '07:00', endTime: '19:00', totalHours: '12.0', hourlyRate: '$91.00', totalAmt: '$1,092.00', isDavidChen: false },
];

// ── Props ────────────────────────────────────────────────────────────────────

interface InvoicePdfPanelProps {
    readonly isVisible: boolean;
    readonly showSuccess: boolean;
}

// ── Component ────────────────────────────────────────────────────────────────

const InvoicePdfPanel = ({ isVisible, showSuccess }: InvoicePdfPanelProps): React.ReactElement => (
    <div
        className={`ipp-panel${isVisible ? ' ipp-panel--visible' : ''}`}
        data-demo-target='invoice-pdf-panel'
    >
        {/* ── Panel header bar ─────────────────────────────────────── */}
        <div className='ipp-panel-header'>
            <span className='ipp-panel-title'>Invoice INV-20260415-KH0044</span>
            <div className='ipp-panel-actions'>
                <button className='ipp-action-btn'><Printer size={9} /> Print</button>
                <button className='ipp-action-btn'><Download size={9} /> Download PDF</button>
            </div>
        </div>

        {/* ── Invoice document ─────────────────────────────────────── */}
        <div className='ipp-doc'>

            {/* Metadata + agency logo */}
            <div className='ipp-meta-row'>
                <div className='ipp-meta-fields'>
                    <div className='ipp-meta-field'>
                        <span className='ipp-meta-key'>Billing cycle</span>
                        <span className='ipp-meta-val'>2026-04-01 to 2026-04-15</span>
                    </div>
                    <div className='ipp-meta-field'>
                        <span className='ipp-meta-key'>Invoice number</span>
                        <span className='ipp-meta-val'>INV-20260415-KH0044</span>
                    </div>
                    <div className='ipp-meta-field'>
                        <span className='ipp-meta-key'>Date of issue</span>
                        <span className='ipp-meta-val'>2026-04-16</span>
                    </div>
                    <div className='ipp-meta-field'>
                        <span className='ipp-meta-key'>Date due</span>
                        <span className='ipp-meta-val'>2026-04-23</span>
                    </div>
                </div>

                <div className='ipp-agency-logo'>
                    <div className='ipp-agency-logo-box'>
                        <span className='ipp-logo-name'>APEX</span>
                        <span className='ipp-logo-sub'>HEALTHCARE{'\n'}STAFFING</span>
                    </div>
                </div>
            </div>

            <div className='ipp-divider' />

            {/* From / Bill To */}
            <div className='ipp-address-row'>
                <div className='ipp-address-block'>
                    <span className='ipp-address-label'>From</span>
                    <span className='ipp-address-name'>Apex Healthcare Staffing Solutions Inc.</span>
                    <span className='ipp-address-line'>200 King Street West, Suite 1600</span>
                    <span className='ipp-address-line'>Toronto, ON M5H 3T4</span>
                    <span className='ipp-address-line'>Canada</span>
                    <span className='ipp-address-line'>416-555-0148</span>
                    <span className='ipp-address-line'>billing@apexhealthstaffing.ca</span>
                    <span className='ipp-address-line'>HST 8941 5273 RT0001</span>
                </div>
                <div className='ipp-address-block'>
                    <span className='ipp-address-label'>Bill to</span>
                    <span className='ipp-address-name'>Kingston Health Sciences Centre</span>
                    <span className='ipp-address-line'>76 Stuart Street</span>
                    <span className='ipp-address-line'>Kingston, ON K7L 2V7</span>
                    <span className='ipp-address-line'>Canada</span>
                </div>
            </div>

            <div className='ipp-divider' />

            {/* Line items table */}
            <table className='ipp-table'>
                <thead>
                    <tr>
                        <th>Date</th>
                        <th>Staff</th>
                        <th>Position</th>
                        <th>Start Time</th>
                        <th>End Time</th>
                        <th>Total Hours</th>
                        <th>Hourly Rate</th>
                        <th>Total Amt</th>
                    </tr>
                </thead>
                <tbody>
                    {LINE_ITEMS.map((item: LineItem, idx: number) => (
                        <tr
                            key={idx}
                            className={item.isDavidChen ? 'ipp-tr--highlight' : ''}
                            style={item.isDavidChen ? { background: '#fffbeb' } : {}}
                        >
                            <td>{item.date}</td>
                            <td style={{ fontWeight: item.isDavidChen ? 700 : 400 }}>{item.staff}</td>
                            <td>{item.position}</td>
                            <td>{item.startTime}</td>
                            <td>{item.endTime}</td>
                            <td>{item.totalHours}</td>
                            <td>{item.hourlyRate}</td>
                            <td className='ipp-td-amount'>{item.totalAmt}</td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {/* Totals */}
            <div className='ipp-totals'>
                <div className='ipp-total-row'>
                    <span className='ipp-total-label'>Subtotal</span>
                    <span className='ipp-total-value'>$5,900.00</span>
                </div>
                <div className='ipp-total-row'>
                    <span className='ipp-total-label'>HST (13%)</span>
                    <span className='ipp-total-value'>$767.00</span>
                </div>
                <div className='ipp-total-divider' />
                <div className='ipp-total-row'>
                    <span className='ipp-grand-total-label'>Total Due</span>
                    <span className='ipp-grand-total-value'>$6,667.00</span>
                </div>
            </div>

            {/* Send invoice button */}
            <button
                className='ipp-send-btn'
                data-demo-target='invoice-send-btn'
            >
                <Send size={12} />
                Send Invoice to Client
            </button>

            {/* Footer */}
            <div className='ipp-footer'>
                <span className='ipp-footer-powered'>Powered by Worky Software Solutions Inc.</span>
                <span className='ipp-footer-worky'>worky</span>
            </div>

        </div>

        {/* ── Success overlay ───────────────────────────────────────── */}
        <div className={`ipp-success${showSuccess ? ' ipp-success--visible' : ''}`}>
            {showSuccess && (
                <svg className='ipp-check-svg' viewBox='0 0 60 60'>
                    <circle
                        className='ipp-check-bg'
                        cx='30' cy='30' r='28'
                        fill='#dcfce7'
                    />
                    <circle
                        className='ipp-check-ring'
                        cx='30' cy='30' r='28'
                        fill='none'
                        stroke='#16a34a'
                        strokeWidth='2.5'
                    />
                    <path
                        className='ipp-check-path'
                        d='M18 30 l9 9 l15 -17'
                        fill='none'
                        stroke='#16a34a'
                        strokeWidth='3'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                    />
                </svg>
            )}
            <span className='ipp-success-label'>Invoice Sent!</span>
            <span className='ipp-success-sub'>Kingston Health Sciences Centre has been notified</span>
        </div>

    </div>
);

export { InvoicePdfPanel };
export type { InvoicePdfPanelProps };
